import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { IoSearchOutline } from "react-icons/io5";
import CustomerOrderHistoryModal from "../../components/modals/CustomerOrderHistoryModal";
import Pagination from "../../components/Pagination";
import toast from "react-hot-toast";

interface FlexibleUser {
  id: string;
  sn: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  dateRegistered: string;
}

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr || typeof dateStr !== "string") {
    return "—";
  }
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const mapRow = (u: any, index: number, page: number, limit: number): FlexibleUser => {
  const id = String(u?._id ?? u?.id ?? u?.userId ?? index);
  const first = typeof u?.firstName === "string" ? u.firstName : "";
  const last = typeof u?.lastName === "string" ? u.lastName : "";
  const nameFromParts = `${first} ${last}`.trim();
  const name = String(u?.name ?? u?.fullName ?? (nameFromParts || "—"));
  const email = String(u?.email ?? u?.userEmail ?? "—");
  const phone = String(u?.phone ?? u?.phoneNumber ?? u?.mobile ?? "—");
  const address = String(
    u?.address ?? u?.contactAddress ?? u?.residentialAddress ?? u?.homeAddress ?? "—"
  );
  const rawDate =
    typeof u?.createdAt === "string"
      ? u.createdAt
      : typeof u?.dateRegistered === "string"
        ? u.dateRegistered
        : typeof u?.registeredAt === "string"
          ? u.registeredAt
          : undefined;
  const sn = String((page - 1) * limit + index + 1).padStart(2, "0");
  return {
    id,
    sn,
    name,
    email,
    phone,
    address,
    dateRegistered: formatDate(rawDate),
  };
};

const extractList = (body: any): any[] => {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.users)) return body.users;
  if (Array.isArray(body.results)) return body.results;
  if (body.data && Array.isArray(body.data.users)) return body.data.users;
  if (body.data && Array.isArray(body.data.results)) return body.data.results;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    const inner = body.data;
    if (Array.isArray(inner.users)) return inner.users;
    if (Array.isArray(inner.results)) return inner.results;
  }
  return [];
};

const extractPagination = (body: any) => {
  const inner = body?.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : null;
  const p =
    body?.pagination ??
    body?.data?.pagination ??
    inner?.pagination ??
    {};
  const totalPages = Math.max(1, Number(p.totalPages ?? p.pages ?? 1) || 1);
  const total = Number(p.total ?? p.totalCount ?? p.count ?? p.totalResults ?? 0);
  return { totalPages, total };
};

const FlexibleUsers = () => {
  const auth = sessionStorage.getItem("authState");
  const authState = auth ? JSON.parse(auth) : null;
  const token = authState?.token ?? "";

  const [searchEmail, setSearchEmail] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef<number | null>(null);
  const [year, setYear] = useState("2026");
  const [month, setMonth] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderHistoryCustomerId, setOrderHistoryCustomerId] = useState<string | null>(null);

  const [users, setUsers] = useState<FlexibleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      searchDebounceRef.current = null;
      setDebouncedSearch(searchEmail.trim().toLowerCase());
    }, 350);
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchEmail]);

  const skipFilterPageReset = useRef(true);
  useLayoutEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, year, month]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setUsers([]);
      setError("Not signed in");
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);

    const params: Record<string, string | number> = {};
    if (year) params.year = year;
    if (month) params.month = month;
    if (debouncedSearch) {
      params.email = debouncedSearch;
    } else {
      params.page = currentPage;
      params.limit = itemsPerPage;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}/dashboard/flexible-users`, {
        headers: { Authorization: token },
        params,
        signal: ac.signal,
      })
      .then((res) => {
        const payload = res.data ?? {};
        const list = extractList(payload);
        const { totalPages: tp, total } = extractPagination(payload);
        const effectiveTotal =
          total > 0 ? total : (currentPage - 1) * itemsPerPage + list.length;

        setUsers(list.map((u, i) => mapRow(u, i, currentPage, itemsPerPage)));
        setTotalPages(tp);
        setTotalResults(effectiveTotal);
      })
      .catch((err: any) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        const message =
          err?.response?.data?.message || err?.message || "Failed to load flexible users";
        setError(String(message));
        setUsers([]);
        setTotalPages(1);
        setTotalResults(0);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [token, currentPage, itemsPerPage, year, month, debouncedSearch]);

  const handleCopyClick = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy");
    }
  };

  const goToPage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  return (
    <main className="w-full cursor-default">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Flexible Users</h1>
          <p className="text-gray-500 text-xs sm:text-sm">
            See all newly registered Flexible Users
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-lg">
            <IoSearchOutline className="w-5 h-5 absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search E-mail"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#008060]"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Year:</span>
              <select 
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#008060] bg-white cursor-pointer"
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Month (optional):</span>
              <select 
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#008060] bg-white cursor-pointer"
              >
                <option value="">All</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
                <option value="April">April</option>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-100 rounded-lg">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 font-medium">
              <tr className="border-b border-gray-100">
                <th className="px-4 py-4 whitespace-nowrap">S/N</th>
                <th className="px-4 py-4 whitespace-nowrap">Name</th>
                <th className="px-4 py-4 whitespace-nowrap">E-mail</th>
                <th className="px-4 py-4 whitespace-nowrap">Phone Number</th>
                <th className="px-4 py-4 whitespace-nowrap">Contact Address</th>
                <th className="px-4 py-4 whitespace-nowrap">Date Registered</th>
                <th className="px-4 py-4 whitespace-nowrap text-center">Order History</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-red-500 text-sm">
                    {error}
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No flexible users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-4 text-gray-800">{user.sn}</td>
                    <td className="px-4 py-4 text-gray-800">{user.name}</td>
                    <td className="px-4 py-4 text-gray-600">
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                        onClick={() => handleCopyClick(user.email)}
                        title="Click to copy"
                      >
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        {user.email}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-600">
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                        onClick={() => handleCopyClick(user.phone)}
                        title="Click to copy"
                      >
                        <svg
                          className="w-4 h-4 text-[#008060]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        {user.phone}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-600">{user.address}</td>
                    <td className="px-4 py-4 text-gray-600">{user.dateRegistered}</td>
                    <td className="px-4 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setOrderHistoryCustomerId(user.id);
                          setIsModalOpen(true);
                        }}
                        className="text-[#008060] font-medium hover:underline focus:outline-none underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && !error && totalResults > 0 && (
          <div className="mt-6">
            <Pagination
              itemsPerPage={itemsPerPage}
              length={totalResults}
              handlePagination={goToPage}
              currentPage={currentPage}
              prevPage={prevPage}
              nextPage={nextPage}
              responseTotalPages={totalPages}
            />
          </div>
        )}
      </div>

      <CustomerOrderHistoryModal
        isOpen={isModalOpen}
        customerId={orderHistoryCustomerId}
        onClose={() => {
          setIsModalOpen(false);
          setOrderHistoryCustomerId(null);
        }}
      />
    </main>
  );
};

export default FlexibleUsers;
